import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  changeEmailVerification,
  resendVerificationCode,
  verifyUser,
} from "../../redux/Actions/userActions";
import LanguageToggle from "../LanguageToggle/LanguageToggle";
import LoadingSpinner from "../Loader/LoadingSpinner";
import Meta from "../Meta/Meta";
import { setError } from "./../../redux/Actions/errorActions";

function Verification() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(["ui"]);
  const isArabic = i18n.language === "ar";
  const { loading, pendingEmail: email } = useSelector((s) => s.auth || {});
  const location = useLocation();
  const emailFromUrl = new URLSearchParams(location.search).get("email") || "";
  const effectiveEmail = email || emailFromUrl;

  const [digits, setDigits] = useState(["", "", "", ""]);
  const inputsRef = useRef([]);
  const [validationError, setValidationError] = useState("");
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [changeEmailValue, setChangeEmailValue] = useState("");
  const [changeEmailError, setChangeEmailError] = useState("");
  const [changeEmailMessage, setChangeEmailMessage] = useState("");
  const [changeEmailLoading, setChangeEmailLoading] = useState(false);

  // Cooldown logic
  const [resendDisabled, setResendDisabled] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    const savedTime = localStorage.getItem("resendCodeTimestamp");
    if (savedTime) {
      const diff = Math.ceil(
        (parseInt(savedTime, 10) - new Date().getTime()) / 1000
      );
      if (diff > 0) {
        setResendDisabled(true);
        setCooldown(diff);
      } else {
        localStorage.removeItem("resendCodeTimestamp");
      }
    }
  }, []);

  useEffect(() => {
    let interval;
    if (resendDisabled && cooldown > 0) {
      interval = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            setResendDisabled(false);
            localStorage.removeItem("resendCodeTimestamp");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendDisabled, cooldown]);

  const setDigit = (idx, val) => {
    const v = (val || "").replace(/[^\d]/g, "").slice(0, 1);
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && idx < 3) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
      return;
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowRight" && idx < 3) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/[^\d]/g, "");
    if (!text) return;
    const parts = text.slice(0, 4).split("");
    const fill = [...parts, "", "", "", ""].slice(0, 4);
    setDigits(fill);
    inputsRef.current[Math.min(parts.length, 3)]?.focus();
  };

  const verificationCode = digits.join("");

  const handleVerify = async () => {
    if (verificationCode.length !== 4) {
      setValidationError(
        t("verification.form.codeError", {
          ns: "ui",
          defaultValue: "Please enter a valid 8-digit code.",
        })
      );
      return;
    }
    setValidationError("");

    try {
      await dispatch(
        verifyUser({ email: effectiveEmail, code: verificationCode })
      );
      navigate("/login");
    } catch (err) {
      dispatch(
        setError(
          err?.response?.data?.message ||
            t("verification.form.wrongCode", {
              ns: "ui",
              defaultValue: "Verification failed. Please try again.",
            }),
          "error"
        )
      );
    }
  };

  const handleResend = () => {
    if (resendDisabled) return;

    dispatch(
      resendVerificationCode(
        effectiveEmail,
        t("verification.form.resendSuccess", { ns: "ui" })
      )
    );

    const future = new Date().getTime() + 5 * 60 * 1000; // 5 minutes
    localStorage.setItem("resendCodeTimestamp", future.toString());
    setResendDisabled(true);
    setCooldown(5 * 60);
  };

  const handleOpenChangeEmail = () => {
    setChangeEmailValue(effectiveEmail || "");
    setChangeEmailError("");
    setChangeEmailMessage("");
    setChangeEmailOpen(true);
  };

  const handleChangeEmail = async () => {
    const nextEmail = changeEmailValue.trim();
    if (!nextEmail) {
      setChangeEmailError(t("login.emailRequired", { ns: "ui" }));
      return;
    }
    if (!emailRegex.test(nextEmail)) {
      setChangeEmailError(t("login.emailInvalid", { ns: "ui" }));
      return;
    }
    setChangeEmailError("");
    setChangeEmailMessage("");
    setChangeEmailLoading(true);
    try {
      await dispatch(changeEmailVerification(effectiveEmail, nextEmail));
      setChangeEmailMessage(
        t("verification.form.changeEmailSuccess", {
          ns: "ui",
          email: nextEmail,
        })
      );
    } catch (err) {
      setChangeEmailError(
        err?.response?.data?.message ||
          t("verification.form.changeEmailFailed", { ns: "ui" })
      );
    } finally {
      setChangeEmailLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  return (
    <>
      <Meta
        title="ALGermanist - Verify Email"
        description="Enter your verification code to activate your ALGermanist account and start learning."
        keywords="verify email, account verification, ALGermanist"
      />
      <Grid
        container
        sx={{
          height: "100vh",
        }}
      >
        {loading && <LoadingSpinner loading />}

        {/* RIGHT SIDE (FORM) - NOW FIRST */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            backgroundColor: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 3,
          }}
        >
          <Container maxWidth="xs" sx={{ px: 0, direction: isArabic ? "rtl" : "ltr" }}>
            {/* Language switcher */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <LanguageToggle />
            </Box>

            {/* Mobile Only: Top Logo */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                justifyContent: "flex-start",
                mt: 1,
                mb: 3,
              }}
            >
              <Box component={Link} to="/" sx={{ display: "inline-flex" }}>
                <Box
                  component="img"
                  src="/al-germanist-logo.png"
                  alt="ALGermanist"
                  sx={{ height: 52 }}
                />
              </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ width: "100%", maxWidth: 420, mx: "auto" }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  mt: { xs: 0, md: 12 },
                  mb: 2,
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {t("verification.form.title", { ns: "ui" })}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  textAlign: isArabic ? "right" : "left",
                  color: "text.secondary",
                  mb: 3,
                  fontSize: { xs: 14, sm: 16 },
                }}
              >
                {t("verification.form.subtitle", {
                  ns: "ui",
                  email: effectiveEmail,
                })}
              </Typography>

              {/* 4-digit input fields */}
              <Box
                onPaste={handlePaste}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  direction: "ltr",
                  gap: { xs: 1, sm: 2 },
                  mb: 4,
                }}
              >
                {digits.map((d, idx) => (
                  <TextField
                    key={idx}
                    inputRef={(el) => (inputsRef.current[idx] = el)}
                    value={d}
                    onChange={(e) => setDigit(idx, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(idx, e)}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 1,
                      style: {
                        textAlign: "center",
                        fontSize: "1.6rem",
                        fontWeight: 700,
                        width: "100%",
                        maxWidth: 64,
                        height: 64,
                      },
                    }}
                    sx={{
                      width: { xs: 60, sm: 80 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "#F4F5F6",
                        height: { xs: 60, sm: 80 },
                        "& fieldset": { borderColor: "#E3E7EA" },
                        "&:hover fieldset": { borderColor: "#C9D1D5" },
                        "&.Mui-focused fieldset": { borderColor: "#FFD600" },
                      },
                      "& input": {
                        height: "100%",
                        boxSizing: "border-box",
                      },
                    }}
                  />
                ))}
              </Box>

              {validationError && (
                <Typography
                  color="error"
                  sx={{ textAlign: "center", mb: 2, fontSize: 14 }}
                >
                  {validationError}
                </Typography>
              )}

              <Button
                variant="contained"
                color="primary"
                disabled={loading}
                fullWidth
                onClick={handleVerify}
                sx={{ mt: 4, mb: 2 }}
              >
                {t("verification.form.verifyBtn", { ns: "ui" })}
              </Button>

              <Tooltip
                title={
                  resendDisabled
                    ? t("verification.form.resendCooldown", { ns: "ui" })
                    : ""
                }
                arrow
                disableHoverListener={!resendDisabled}
              >
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={handleResend}
                    disabled={resendDisabled || loading}
                    sx={{ mb: 4 }}
                  >
                    {t("verification.form.resendBtn", { ns: "ui" })}
                    {resendDisabled && ` (${formatTime(cooldown)})`}
                  </Button>
                </span>
              </Tooltip>

              <Button
                variant="outlined"
                fullWidth
                onClick={handleOpenChangeEmail}
                sx={{
                  mb: 2,
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                {t("verification.form.changeEmailCta", { ns: "ui" })}
              </Button>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("verification.form.alreadyVerifiedPrefix", {
                    ns: "ui",
                  })}{" "}
                  <Link
                    to="/login"
                    style={{ color: "#245D51", fontWeight: 700 }}
                  >
                    {t("verification.form.login", { ns: "ui" })}
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Container>
        </Grid>

        {/* LEFT SIDE (HERO) - NOW SECOND */}
        <Grid
          item
          xs={false}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-start",
            textAlign: "left",
            px: { md: 5, lg: 7 },
            py: { md: 4, lg: 5 },
            overflow: "hidden",
            backgroundImage:
              "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.02) 45%, rgba(0,0,0,0.72) 100%), url('/verification-heidelberg.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <Box
            onClick={() => navigate("/")}
            sx={{ alignSelf: "center", cursor: "pointer", mt: -1 }}
          >
            <Box
              component="img"
              src="/al-germanist-logo.png"
              alt="ALGermanist"
              sx={{
                height: { md: 82, lg: 98 },
                maxWidth: 330,
                objectFit: "contain",
                mixBlendMode: "multiply",
              }}
            />
          </Box>
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              maxWidth: 560,
              pb: 2,
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                lineHeight: 1.3,
                color: "#fff",
                m: 0,
                fontSize: { md: 30, lg: 38 },
                textShadow: "0 3px 16px rgba(0,0,0,0.75)",
              }}
            >
              {t("login.sloganLine1", { ns: "ui" })}{" "}
              <Box component="span" sx={{ color: "#FFD000" }}>
                {t("login.sloganLine2", { ns: "ui" })}
              </Box>{" "}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={changeEmailOpen}
        onClose={() => setChangeEmailOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ textAlign: isArabic ? "right" : "left", direction: isArabic ? "rtl" : "ltr" }}>
          {t("verification.form.changeEmailTitle", { ns: "ui" })}
        </DialogTitle>
        <DialogContent sx={{ textAlign: isArabic ? "right" : "left", direction: isArabic ? "rtl" : "ltr" }}>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            {t("verification.form.changeEmailBody", { ns: "ui" })}
          </Typography>
          <TextField
            fullWidth
            label={t("verification.form.changeEmailLabel", { ns: "ui" })}
            type="email"
            value={changeEmailValue}
            onChange={(e) => setChangeEmailValue(e.target.value)}
            error={Boolean(changeEmailError)}
            helperText={changeEmailError}
            sx={{
              "& label": {
                left: isArabic ? "auto" : "14px",
                right: isArabic ? "32px" : "auto",
                transformOrigin: isArabic ? "top right" : "top left",
              },
              "& legend": {
                textAlign: isArabic ? "right" : "left",
              },
            }}
          />
          {changeEmailMessage && (
            <Typography
              sx={{
                mt: 2,
                color: "success.main",
                textAlign: isArabic ? "right" : "left",
                lineHeight: 1.6,
                overflowWrap: "normal",
                wordBreak: "normal",
              }}
            >
              {changeEmailMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setChangeEmailOpen(false)}
            disabled={changeEmailLoading}
          >
            {t("verification.form.changeEmailCancel", { ns: "ui" })}
          </Button>
          <Button
            variant="contained"
            onClick={handleChangeEmail}
            disabled={changeEmailLoading}
          >
            {changeEmailLoading
              ? t("verification.form.changeEmailLoading", { ns: "ui" })
              : t("verification.form.changeEmailSubmit", { ns: "ui" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Verification;
